// The Guardrails screen (SCR-155, SCR-594, REQ-242): a Bedrock guardrail the proxy attaches to
// requests, configuration only, on both shells. Off by default. The Guardrails card lists what
// can be applied: the definition these settings hold (the IaC builds it), the stored existing id,
// and the guardrails an installation's account already has (listInstallationGuardrails,
// best-effort), each with Apply; "Create new guardrail" opens the definition editor below. Then
// which tools it covers, trace and stream mode. Source and definition changes take effect after a
// redeploy (the field annotation says so); the screen says so before saving. What the guardrail
// did (its interventions) is Activity's, not this screen's.
//
// The scope is the shell's: the billing admin offers the scope selector (the org default or one
// installation, REQ-214); the proxy names its own installation and gets no picker, so the screen
// writes the org default and lists that installation's account guardrails (SCR-594). The surface
// decides what the principal may edit (`fieldMode`); while the proxy's API is unreachable
// (ADR-104) nothing saves.
import { useEffect, useState } from "preact/hooks";
import { useApi } from "../../api/context";
import { ApiError } from "../../api/errors";
import type { OrgContext, Surface } from "../../api/org-context";
import { useHost } from "../../host/host";
import { useReadOnly } from "../../host/readonly";
import { Checkbox, Field, Fieldset } from "../../primitives/field";
import { fieldErrorsFrom, hasErrors } from "../../primitives/forms";
import type { HelpId } from "../../primitives/help";
import { Card, Chip } from "../../primitives/page";
import { StaleNote } from "../../primitives/stale-note";
import { ErrorBanner, Loading } from "../../primitives/states";
import { FieldRenderer } from "../../settings/field-renderer";
import { getPath, isInherited, isObject, merge, setPath, type Json, type JsonObject } from "../../settings/inherit";
import { fieldMode, pinFor } from "../../settings/pins";
import { ScopeSelector, type ScopeOption } from "../../settings/scope-selector";
import { FIELDS, validate, type FieldDescriptor, type SettingsErrors } from "../../settings/validate";
import { loadScope, saveScope } from "../models/models-tab";
import {
  addPii,
  addRegex,
  addTopic,
  appliedSource,
  APPLY_TO,
  applySource,
  applyToOf,
  CONTENT_FILTERS,
  createDefinition,
  definitionJson,
  definitionOf,
  existingErrors,
  GROUNDING_NOTE,
  guardrailRows,
  LIMITS,
  listingNote,
  parseLines,
  pendingRedeploy,
  PII_ACTIONS,
  PROMPT_ATTACK_OUTPUT,
  redeployText,
  REDEPLOY_NOTE,
  removePii,
  removeRegex,
  removeTopic,
  setFilterStrength,
  stopApplying,
  STRENGTHS,
  toggleApplyTo,
  updatePii,
  updateRegex,
  updateTopic,
  validateGuardrails,
  versionLabel,
  type Definition,
  type FilterKey,
  type GuardrailListing,
  type GuardrailRow,
  type GuardrailSource,
  type RowVersions,
  type PiiAction,
  type Strength,
} from "./model";

const h = (id: string) => id as HelpId;
const ORG_SCOPE_NOTE = "Choose an installation to see the guardrails in its account.";
/** The proxy has no picker: until it names its own installation there is nothing to list. */
const UNREPORTED_NOTE = "Waiting for the proxy's first report. The guardrails in its account are listed once it has named its installation.";
const STALE_NOTE_ID = "guardrails-stale-note";

function unexpected(error: unknown): ApiError {
  return error instanceof ApiError ? error : new ApiError(0, "internal", "Something went wrong.");
}

function field(path: string): FieldDescriptor {
  const found = FIELDS.find((f) => f.path === path);
  if (!found) throw new Error(`settings field ${path} is not in the schema`);
  return found;
}

/** What the card says about the account's own guardrails while, or instead of, listing them. */
type Listing = { state: "org" } | { state: "unreported" } | { state: "loading" } | { state: "failed" } | { state: "loaded"; listing: GuardrailListing };

/**
 * The installation's Bedrock guardrails ("" at the org default and null on a proxy that has not
 * named its installation list none). Best-effort: a failed call leaves the card with the
 * definition and the typed id, it does not block the settings form.
 */
function useListing(orgId: string, installationId: string | null): Listing {
  const client = useApi();
  const [listing, setListing] = useState<Listing>({ state: installationId === null ? "unreported" : "org" });
  useEffect(() => {
    if (installationId === "" || installationId === null) {
      setListing({ state: installationId === null ? "unreported" : "org" });
      return;
    }
    let live = true;
    setListing({ state: "loading" });
    client
      .get("/v1/orgs/{orgId}/installations/{installationId}/guardrails", { path: { orgId, installationId } })
      .then((found) => live && setListing({ state: "loaded", listing: found }))
      .catch(() => live && setListing({ state: "failed" }));
    return () => {
      live = false;
    };
  }, [client, orgId, installationId]);
  return listing;
}

export interface GuardrailsScreenProps {
  context: OrgContext;
  /**
   * The proxy's own installation (its `/api/me` `installationId`, never displayed): the screen
   * edits the org default and lists this installation's account guardrails, with no picker.
   * Absent on the billing admin, whose scope selector picks the scope.
   */
  installationId?: string | undefined;
}

export function GuardrailsScreen({ context, installationId }: GuardrailsScreenProps) {
  const { orgId, surface } = context;
  const client = useApi();
  const host = useHost();
  const { readOnly } = useReadOnly();
  const picks = host.kind === "allcode-admin";
  const [installations, setInstallations] = useState<ScopeOption[] | null>(picks ? null : []);
  const [scope, setScope] = useState("");
  const [loaded, setLoaded] = useState<Awaited<ReturnType<typeof loadScope>> | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const listing = useListing(orgId, picks ? scope : (installationId ?? null));

  // "Saved." lives here, not in the editor: a save reloads the scope, which remounts the
  // editor (keyed on the version) and would take its own status line with it (TASK-195).
  const [saved, setSaved] = useState(false);
  const load = () => {
    setError(null);
    setLoaded(null);
    if (picks) {
      client
        .get("/v1/orgs/{orgId}/installations", { path: { orgId }, query: { limit: 500 } })
        .then((page) => setInstallations(page.items.map((item) => ({ id: item.id, label: item.name }))))
        .catch((err: unknown) => setError(unexpected(err)));
    }
    loadScope(client, orgId, scope).then(setLoaded).catch((err: unknown) => setError(unexpected(err)));
  };
  useEffect(load, [client, orgId, scope]);

  if (error) return <ErrorBanner error={error} onRetry={load} />;
  if (!installations || !loaded) return <Loading label="Loading guardrail settings…" />;
  return (
    <>
      {readOnly && <StaleNote id={STALE_NOTE_ID} />}
      {picks && (
        <ScopeSelector
          installations={installations}
          value={scope}
          onChange={(next) => {
            setSaved(false);
            setScope(next);
          }}
        />
      )}
      {saved && (
        <p class="field-hint" role="status">
          Saved.
        </p>
      )}
      <GuardrailsEditor
        key={`${scope}:${loaded.version}`}
        orgId={orgId}
        surface={surface}
        scope={scope}
        scopeName={scope === "" ? null : (installations.find((i) => i.id === scope)?.label ?? "this installation")}
        readOnly={readOnly}
        loaded={loaded}
        listing={listing}
        onSaved={() => {
          setSaved(true);
          load();
        }}
      />
    </>
  );
}

interface GuardrailsEditorProps {
  orgId: string;
  surface: Surface;
  scope: string;
  /** The picked installation's name, for the form's accessible name; null at the org default. */
  scopeName: string | null;
  readOnly: boolean;
  loaded: Awaited<ReturnType<typeof loadScope>>;
  listing: Listing;
  onSaved: () => void;
}

function GuardrailsEditor({ orgId, surface, scope, scopeName, readOnly, loaded, listing, onSaved }: GuardrailsEditorProps) {
  const client = useApi();
  const [draft, setDraft] = useState<JsonObject>(loaded.document);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [serverErrors, setServerErrors] = useState<SettingsErrors>({});
  const [failure, setFailure] = useState<ApiError | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const inheritedDoc = loaded.inherited;
  const effective = (merge(inheritedDoc ?? {}, draft) as JsonObject | undefined) ?? {};
  const errors = { ...validate(effective), ...validateGuardrails(effective), ...serverErrors };
  const changed = JSON.stringify(draft) !== JSON.stringify(loaded.document);
  const blocked = readOnly || hasErrors(errors) || saving || !changed;
  const savedEffective = merge(inheritedDoc ?? {}, loaded.document);
  const notice = redeployText(pendingRedeploy(savedEffective, effective));

  const locked = (path: string) => fieldMode(field(path), surface, pinFor(loaded.pins, path)).kind === "readonly";
  const sourceLocked = locked("guardrails.enabled") || locked("guardrails.mode") || locked("guardrails.existing");
  const definitionMode = fieldMode(field("guardrails.definition"), surface, pinFor(loaded.pins, "guardrails.definition"));
  const definitionLocked = definitionMode.kind === "readonly";
  const hasDefinition = isObject(getPath(effective, "guardrails.definition"));
  const showsEditor = hasDefinition && (editing || getPath(effective, "guardrails.mode") !== "existing");

  const change = (next: (current: JsonObject) => JsonObject) => {
    setDone(null);
    setDraft(next);
  };
  const set = (path: string, value: Json | undefined) => change((current) => setPath(current, path, value));
  const setDefinition = (definition: Definition) => set("guardrails.definition", definitionJson(definition));

  const save = () => {
    if (blocked) return;
    setSaving(true);
    setFailure(null);
    setServerErrors({});
    saveScope(client, orgId, scope, loaded, draft)
      .then(() => {
        onSaved();
      })
      .catch((err: unknown) => {
        const api = unexpected(err);
        setServerErrors(fieldErrorsFrom(api));
        setFailure(api);
      })
      .finally(() => setSaving(false));
  };

  const render = (path: string) => {
    const descriptor = field(path);
    return (
      <FieldRenderer
        field={descriptor}
        value={getPath(effective, path)}
        mode={fieldMode(descriptor, surface, pinFor(loaded.pins, path))}
        inherited={inheritedDoc !== undefined && isInherited(draft, path)}
        error={errors[path]}
        onChange={(next) => set(path, next)}
      />
    );
  };

  return (
    <form
      aria-label={`Guardrail settings${scopeName ? ` for ${scopeName}` : ""}`}
      onSubmit={(event) => {
        event.preventDefault();
        save();
      }}
    >
      <GuardrailList
        applying={appliedSource(effective) !== null}
        rows={guardrailRows(effective, listing.state === "loaded" ? listing.listing.items : [])}
        listing={listing}
        locked={sourceLocked}
        createLocked={sourceLocked || definitionLocked}
        inherited={inheritedDoc !== undefined && isInherited(draft, "guardrails.enabled")}
        existingError={errors["guardrails.existing.id"] ?? errors["guardrails.existing.version"]}
        onApply={(source) => change((current) => applySource(current, source))}
        onStop={() => change(stopApplying)}
        onEdit={showsEditor ? undefined : () => setEditing(true)}
        onCreate={() => {
          setEditing(true);
          change(createDefinition);
        }}
      />

      {showsEditor && <DefinitionEditor definition={definitionOf(effective)} errors={errors} locked={definitionLocked} lockedNote={definitionMode.kind === "readonly" ? definitionMode.note : ""} onChange={setDefinition} />}

      <Card title="How it applies" help={h("settings.guardrails")}>
        <ApplyTo value={applyToOf(effective)} error={errors["guardrails.apply_to"]} inherited={inheritedDoc !== undefined && isInherited(draft, "guardrails.apply_to")} onChange={(next) => set("guardrails.apply_to", next)} />
        {render("guardrails.trace")}
        {render("guardrails.stream_processing")}
      </Card>

      {notice && (
        <p class="field-hint" role="status">
          <Chip tone="amber">needs redeploy</Chip> {notice}
        </p>
      )}
      {failure && <ErrorBanner error={failure} details={true} />}
      <div class="row">
        <button type="submit" class="button button-primary" disabled={blocked} aria-describedby={readOnly ? STALE_NOTE_ID : undefined}>
          {saving ? "Saving" : "Save"}
        </button>
        {done && (
          <span class="field-hint" role="status">
            {done}
          </span>
        )}
      </div>
    </form>
  );
}

interface ListProps {
  applying: boolean;
  rows: GuardrailRow[];
  listing: Listing;
  locked: boolean;
  createLocked: boolean;
  inherited: boolean;
  existingError: string | undefined;
  onApply(source: GuardrailSource): void;
  onStop(): void;
  /** Opens the definition editor; absent while it is open. */
  onEdit: (() => void) | undefined;
  onCreate(): void;
}

function listingText(listing: Listing): string | null {
  if (listing.state === "org") return ORG_SCOPE_NOTE;
  if (listing.state === "unreported") return UNREPORTED_NOTE;
  if (listing.state === "loading") return "Listing the guardrails in this installation's account…";
  if (listing.state === "failed") return "The guardrails in this account could not be listed.";
  return listingNote(listing.listing);
}

function GuardrailList({ applying, rows, listing, locked, createLocked, inherited, existingError, onApply, onStop, onEdit, onCreate }: ListProps) {
  const note = listingText(listing);
  return (
    <Card
      title="Guardrails"
      help={h("settings.guardrails")}
      actions={
        <button type="button" class="button button-secondary" disabled={createLocked} onClick={onCreate}>
          Create new guardrail
        </button>
      }
    >
      <p class="card-note">{REDEPLOY_NOTE}</p>
      {!applying && (
        <p class="card-note" role="status">
          <Chip tone="grey">off</Chip> No guardrail is applied; requests are not screened.
        </p>
      )}
      {inherited && <p class="card-note">Which guardrail applies is inherited from the customer default until you apply or stop one here.</p>}
      {note && (
        <p class="card-note" role={listing.state === "loading" ? "status" : undefined}>
          {note}
        </p>
      )}
      <div class="table-scroll">
        <table class="data-table">
          <caption class="visually-hidden">Guardrails you can apply</caption>
          <thead>
            <tr>
              <th scope="col">Guardrail</th>
              <th scope="col">Status</th>
              <th scope="col">
                <span class="visually-hidden">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) =>
              row.versions ? (
                <ListedRow key={row.key} row={row} versions={row.versions} locked={locked} onApply={onApply} onStop={onStop} />
              ) : (
                <tr key={row.key}>
                  <th scope="row">
                    <span class="stack-cell">
                      {row.title}
                      <span class="cell-sub">{row.detail}</span>
                    </span>
                  </th>
                  <td>
                    <StatusChip applied={row.applied} applicable={row.applicable} />
                  </td>
                  <td>
                    <div class="row-actions">
                      {row.source.kind === "defined" && onEdit && (
                        <button type="button" class="button button-ghost button-sm" onClick={onEdit}>
                          Edit
                        </button>
                      )}
                      <ApplyButton title={row.title} applied={row.applied} disabled={locked || !row.applicable} stopDisabled={locked} onApply={() => onApply(row.source)} onStop={onStop} />
                    </div>
                  </td>
                </tr>
              ),
            )}
            <ByIdRow locked={locked} error={existingError} onApply={onApply} />
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function StatusChip({ applied, applicable, version }: { applied: boolean; applicable: boolean; version?: string }) {
  if (applied) return <Chip tone="green">{version ? `applied ${version}` : "applied"}</Chip>;
  return applicable ? <Chip tone="grey">not applied</Chip> : <Chip tone="red">unusable</Chip>;
}

function ApplyButton({ title, applied, disabled, stopDisabled, onApply, onStop }: { title: string; applied: boolean; disabled: boolean; stopDisabled: boolean; onApply(): void; onStop(): void }) {
  return applied ? (
    <button type="button" class="button button-secondary button-sm" aria-label={`Stop applying ${title}`} disabled={stopDisabled} onClick={onStop}>
      Stop applying
    </button>
  ) : (
    <button type="button" class="button button-secondary button-sm" aria-label={`Apply ${title}`} disabled={disabled} onClick={onApply}>
      Apply
    </button>
  );
}

/** A guardrail the account lists: its versions, a picker (the applied one, else the latest) and Apply pinning the picked one. */
function ListedRow({ row, versions, locked, onApply, onStop }: { row: GuardrailRow; versions: RowVersions; locked: boolean; onApply(source: GuardrailSource): void; onStop(): void }) {
  const [picked, setPicked] = useState(versions.initial);
  useEffect(() => setPicked(versions.initial), [versions.initial]);
  if (row.source.kind !== "existing") return null;
  const { id } = row.source;
  const { newer } = versions;
  const applied = row.applied && picked === row.source.version;
  const pickerId = `gr-version-${id}`;
  return (
    <tr>
      <th scope="row">
        <span class="stack-cell">
          {row.title}
          <span class="cell-sub">{row.detail}</span>
        </span>
        {row.applied && newer && (
          <span class="field-hint">
            {versionLabel(newer)} is newer.{" "}
            <button type="button" class="button button-ghost button-sm" aria-label={`Apply ${versionLabel(newer)} of ${row.title}`} disabled={locked || !row.applicable} onClick={() => onApply({ kind: "existing", id, version: newer })}>
              Apply {versionLabel(newer)}
            </button>
          </span>
        )}
      </th>
      <td>
        <StatusChip applied={row.applied} applicable={row.applicable} version={versionLabel(row.source.version)} />
      </td>
      <td>
        <div class="row-actions">
          <Field id={pickerId} label={`Version of ${row.title}`} help={h("settings.guardrails.existing.version")} hideLabel={true} compact={true}>
            <select id={pickerId} value={picked} disabled={locked} onChange={(e) => setPicked(e.currentTarget.value)}>
              {versions.options.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </Field>
          <ApplyButton title={row.title} applied={applied} disabled={locked || !row.applicable} stopDisabled={locked} onApply={() => onApply({ kind: "existing", id, version: picked })} onStop={onStop} />
        </div>
      </td>
    </tr>
  );
}

/** "Use a guardrail by id": the fallback when the account's guardrails cannot be listed, or it is not one of them. */
function ByIdRow({ locked, error, onApply }: { locked: boolean; error: string | undefined; onApply(source: GuardrailSource): void }) {
  const [id, setId] = useState("");
  const [version, setVersion] = useState("");
  const problems = existingErrors(id.trim(), version.trim());
  return (
    <tr>
      <th scope="row">
        <span class="stack-cell">
          Use a guardrail by id
          <span class="cell-sub">Its id and version, as the Bedrock console shows them.</span>
        </span>
        {error && (
          <p class="field-error" role="alert">
            {error}
          </p>
        )}
      </th>
      <td>
        <div class="row">
          <Field id="gr-by-id" label="Guardrail id" help={h("settings.guardrails.existing.id")} compact={true} error={id !== "" ? problems.id : null}>
            <input id="gr-by-id" type="text" class="input-mono" value={id} maxLength={12} spellcheck={false} disabled={locked} onInput={(e) => setId(e.currentTarget.value)} />
          </Field>
          <Field id="gr-by-version" label="Version" help={h("settings.guardrails.existing.version")} compact={true} error={version !== "" ? problems.version : null}>
            <input id="gr-by-version" type="text" value={version} maxLength={5} spellcheck={false} disabled={locked} onInput={(e) => setVersion(e.currentTarget.value)} />
          </Field>
        </div>
      </td>
      <td>
        <div class="row-actions">
          <button
            type="button"
            class="button button-secondary button-sm"
            aria-label="Apply the guardrail with this id"
            disabled={locked || problems.id !== undefined || problems.version !== undefined}
            onClick={() => onApply({ kind: "existing", id: id.trim(), version: version.trim() })}
          >
            Apply
          </button>
        </div>
      </td>
    </tr>
  );
}

function ApplyTo({ value, error, inherited, onChange }: { value: string[]; error: string | undefined; inherited: boolean; onChange(next: string[]): void }) {
  return (
    <Fieldset label="Applies to" help={h("settings.guardrails.apply_to")} error={error} class={inherited ? "settings-inherited" : undefined}>
      <div class="picker-grid">
        {APPLY_TO.map((option) => (
          <Checkbox key={option.key} id={`set-guardrails-apply-${option.key}`} label={option.label} help={h("settings.guardrails.apply_to")} checked={value.includes(option.key)} onChange={() => onChange(toggleApplyTo(value, option.key))} />
        ))}
      </div>
      {inherited && <Chip tone="grey">inherited</Chip>}
    </Fieldset>
  );
}

interface EditorProps {
  definition: Definition;
  errors: SettingsErrors;
  locked: boolean;
  lockedNote: string;
  onChange(definition: Definition): void;
}

function DefinitionEditor({ definition, errors, locked, lockedNote, onChange }: EditorProps) {
  const err = (path: string) => errors[`guardrails.definition.${path}`];
  return (
    <>
      {locked && (
        <p class="card-note" role="status">
          <Chip tone="amber">read-only</Chip> {lockedNote}
        </p>
      )}
      <Card title="Content filters" help={h("settings.guardrails.definition.content_filters")}>
        <div class="table-scroll">
          <table class="table">
            <thead>
              <tr>
                <th scope="col">Filter</th>
                <th scope="col">Input</th>
                <th scope="col">Output</th>
              </tr>
            </thead>
            <tbody>
              {CONTENT_FILTERS.map(({ key, label }) => (
                <tr key={key}>
                  <th scope="row">{label}</th>
                  <td>
                    <StrengthSelect id={`gr-${key}-input`} label={`${label} input`} value={definition.content_filters[key].input} disabled={locked} onChange={(next) => onChange(setFilterStrength(definition, key, "input", next))} />
                  </td>
                  <td>{key === "prompt_attack" ? <Chip tone="grey" title="Bedrock requires the output strength NONE for prompt attacks.">{PROMPT_ATTACK_OUTPUT} (fixed)</Chip> : <StrengthSelect id={`gr-${key}-output`} label={`${label} output`} value={(definition.content_filters as Record<string, { output: Strength }>)[key]!.output} disabled={locked} onChange={(next) => onChange(setFilterStrength(definition, key as FilterKey, "output", next))} />}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Card title="Denied topics" help={h("settings.guardrails.definition.denied_topics")}>
        {definition.denied_topics.map((topic, i) => (
          <div key={i} class="settings-field">
            <Field id={`gr-topic-${i}-name`} label={`Topic ${i + 1} name`} help={h("settings.guardrails.definition.denied_topics")} error={err(`denied_topics.${i}.name`)}>
              <input id={`gr-topic-${i}-name`} type="text" value={topic.name} maxLength={LIMITS.topicName} disabled={locked} onInput={(e) => onChange(updateTopic(definition, i, { name: e.currentTarget.value }))} />
            </Field>
            <Field id={`gr-topic-${i}-def`} label={`Topic ${i + 1} definition`} help={h("settings.guardrails.definition.denied_topics")} error={err(`denied_topics.${i}.definition`)}>
              <input id={`gr-topic-${i}-def`} type="text" value={topic.definition} maxLength={LIMITS.topicDefinition} disabled={locked} onInput={(e) => onChange(updateTopic(definition, i, { definition: e.currentTarget.value }))} />
            </Field>
            <Field id={`gr-topic-${i}-examples`} label={`Topic ${i + 1} examples`} help={h("settings.guardrails.definition.denied_topics")} hint="One per line, up to 5." error={err(`denied_topics.${i}.examples`)}>
              <textarea id={`gr-topic-${i}-examples`} rows={2} value={topic.examples.join("\n")} disabled={locked} onInput={(e) => onChange(updateTopic(definition, i, { examples: parseLines(e.currentTarget.value) }))} />
            </Field>
            <button type="button" class="button button-secondary" disabled={locked} onClick={() => onChange(removeTopic(definition, i))}>
              Remove topic {i + 1}
            </button>
          </div>
        ))}
        {err("denied_topics") && <p class="field-error" role="alert">{err("denied_topics")}</p>}
        <button type="button" class="button button-secondary" disabled={locked || definition.denied_topics.length >= LIMITS.topics} onClick={() => onChange(addTopic(definition))}>
          Add a topic
        </button>
      </Card>

      <Card title="Word filters" help={h("settings.guardrails.definition.word_filters")}>
        <Checkbox id="gr-profanity" label="Block profanity" help={h("settings.guardrails.definition.word_filters.profanity")} checked={definition.word_filters.profanity} disabled={locked} onChange={(checked) => onChange({ ...definition, word_filters: { ...definition.word_filters, profanity: checked } })} />
        <Field id="gr-words" label="Blocked words and phrases" help={h("settings.guardrails.definition.word_filters.words")} hint="One per line." error={err("word_filters.words")}>
          <textarea id="gr-words" rows={4} value={definition.word_filters.words.join("\n")} disabled={locked} onInput={(e) => onChange({ ...definition, word_filters: { ...definition.word_filters, words: parseLines(e.currentTarget.value) } })} />
        </Field>
      </Card>

      <Card title="Sensitive information" help={h("settings.guardrails.definition.sensitive_information")}>
        <PiiList definition={definition} locked={locked} error={err("sensitive_information.pii_entities")} onChange={onChange} />
        {definition.sensitive_information.regexes.map((regex, i) => (
          <div key={i} class="settings-field">
            <Field id={`gr-regex-${i}-name`} label={`Expression ${i + 1} name`} help={h("settings.guardrails.definition.sensitive_information.regexes")} error={err(`sensitive_information.regexes.${i}.name`)}>
              <input id={`gr-regex-${i}-name`} type="text" value={regex.name} maxLength={LIMITS.regexName} disabled={locked} onInput={(e) => onChange(updateRegex(definition, i, { name: e.currentTarget.value }))} />
            </Field>
            <Field id={`gr-regex-${i}-pattern`} label={`Expression ${i + 1} pattern`} help={h("settings.guardrails.definition.sensitive_information.regexes")} error={err(`sensitive_information.regexes.${i}.pattern`)}>
              <input id={`gr-regex-${i}-pattern`} type="text" value={regex.pattern} maxLength={LIMITS.regexPattern} disabled={locked} onInput={(e) => onChange(updateRegex(definition, i, { pattern: e.currentTarget.value }))} />
            </Field>
            <ActionSelect id={`gr-regex-${i}-action`} label={`Expression ${i + 1} action`} value={regex.action} disabled={locked} onChange={(next) => onChange(updateRegex(definition, i, { action: next }))} />
            <button type="button" class="button button-secondary" disabled={locked} onClick={() => onChange(removeRegex(definition, i))}>
              Remove expression {i + 1}
            </button>
          </div>
        ))}
        {err("sensitive_information.regexes") && <p class="field-error" role="alert">{err("sensitive_information.regexes")}</p>}
        <button type="button" class="button button-secondary" disabled={locked || definition.sensitive_information.regexes.length >= LIMITS.regexes} onClick={() => onChange(addRegex(definition))}>
          Add an expression
        </button>
      </Card>

      <Card title="Contextual grounding" help={h("settings.guardrails.definition.contextual_grounding")}>
        <p class="card-note">{GROUNDING_NOTE}</p>
        {(["grounding", "relevance"] as const).map((name) => (
          <Field key={name} id={`gr-${name}`} label={name === "grounding" ? "Grounding threshold" : "Relevance threshold"} help={h(`settings.guardrails.definition.contextual_grounding.${name}`)} hint="Empty leaves the check off." error={err(`contextual_grounding.${name}`)}>
            <input
              id={`gr-${name}`}
              type="number"
              min={0}
              max={LIMITS.groundingMax}
              step={0.01}
              value={definition.contextual_grounding[name] ?? ""}
              disabled={locked}
              onInput={(e) => onChange({ ...definition, contextual_grounding: { ...definition.contextual_grounding, [name]: e.currentTarget.value === "" ? null : Number(e.currentTarget.value) } })}
            />
          </Field>
        ))}
      </Card>

      <Card title="Blocked messages" help={h("settings.guardrails.definition.blocked_input_message")}>
        {(["blocked_input_message", "blocked_output_message"] as const).map((name) => (
          <Field key={name} id={`gr-${name}`} label={name === "blocked_input_message" ? "When a request is blocked" : "When a response is blocked"} help={h(`settings.guardrails.definition.${name}`)} error={err(name)}>
            <textarea id={`gr-${name}`} rows={2} value={definition[name]} maxLength={LIMITS.message} disabled={locked} onInput={(e) => onChange({ ...definition, [name]: e.currentTarget.value })} />
          </Field>
        ))}
      </Card>
    </>
  );
}

function StrengthSelect({ id, label, value, disabled, onChange }: { id: string; label: string; value: Strength; disabled: boolean; onChange(next: Strength): void }) {
  return (
    <Field id={id} label={label} help={h("settings.guardrails.definition.content_filters")} hideLabel={true}>
      <select id={id} value={value} disabled={disabled} onChange={(e) => onChange(e.currentTarget.value as Strength)}>
        {STRENGTHS.map((s) => (
          <option key={s} value={s}>
            {s}
          </option>
        ))}
      </select>
    </Field>
  );
}

function ActionSelect({ id, label, value, disabled, onChange }: { id: string; label: string; value: PiiAction; disabled: boolean; onChange(next: PiiAction): void }) {
  return (
    <Field id={id} label={label} help={h("settings.guardrails.definition.sensitive_information")}>
      <select id={id} value={value} disabled={disabled} onChange={(e) => onChange(e.currentTarget.value as PiiAction)}>
        {PII_ACTIONS.map((a) => (
          <option key={a} value={a}>
            {a === "BLOCK" ? "Block" : "Anonymize"}
          </option>
        ))}
      </select>
    </Field>
  );
}

function PiiList({ definition, locked, error, onChange }: { definition: Definition; locked: boolean; error: string | undefined; onChange(definition: Definition): void }) {
  const [entry, setEntry] = useState("");
  const entities = definition.sensitive_information.pii_entities;
  return (
    <div class="settings-field">
      {entities.map((entity, i) => (
        <div key={entity.type} class="row">
          <code>{entity.type}</code>
          <ActionSelect id={`gr-pii-${i}`} label={`${entity.type} action`} value={entity.action} disabled={locked} onChange={(next) => onChange(updatePii(definition, i, next))} />
          <button type="button" class="button button-secondary" disabled={locked} onClick={() => onChange(removePii(definition, i))}>
            Remove {entity.type}
          </button>
        </div>
      ))}
      <Field id="gr-pii-new" label="Personal information type" help={h("settings.guardrails.definition.sensitive_information.pii_entities")} hint="A Bedrock entity type, such as EMAIL or US_SOCIAL_SECURITY_NUMBER." error={error}>
        <input id="gr-pii-new" type="text" value={entry} disabled={locked} onInput={(e) => setEntry(e.currentTarget.value)} />
      </Field>
      <button
        type="button"
        class="button button-secondary"
        disabled={locked || entry.trim() === "" || entities.length >= LIMITS.piiEntities}
        onClick={() => {
          onChange(addPii(definition, entry));
          setEntry("");
        }}
      >
        Add type
      </button>
    </div>
  );
}
